import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { 
  Camera, 
  LayoutDashboard, 
  Calendar, 
  Image, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  CreditCard,
  Wallet,
  FileText,
  Tag,
  FileSignature,
  HelpCircle,
  AlertTriangle,
  Lock,
  Mail,
  BarChart3,
  Clock
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../../App";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import NotificationBell from "../../components/NotificationBell";
import AgreementReacknowledgementModal from "../../components/AgreementReacknowledgementModal";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import axios from "axios";
import { toast } from "sonner";

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [branding, setBranding] = useState({
    company_name: null,
    logo_url: null,
    user_name: null,
    user_picture: null,  // Add user picture from profile
    slug: null  // Add slug for public profile URL
  });
  const [accountStatus, setAccountStatus] = useState({
    account_status: "active",
    subscription_status: "active",
    days_until_suspension: null
  });
  
  // Check if user is currently in onboarding flow
  const isOnboarding = location.pathname.startsWith("/dashboard/onboarding");

  // Allowed paths when account is restricted
  const ALLOWED_PATHS_WHEN_RESTRICTED = [
    "/dashboard",
    "/dashboard/billing",
    "/help"
  ];

  const isPathAllowed = useCallback((path) => {
    if (accountStatus.account_status !== "restricted" && accountStatus.account_status !== "suspended") {
      return true;
    }
    return ALLOWED_PATHS_WHEN_RESTRICTED.some(allowed => 
      path === allowed || path.startsWith(allowed + "/")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountStatus.account_status]);

  const refreshBranding = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/photographer/profile`, { withCredentials: true });
      setBranding({
        company_name: response.data.company_name,
        logo_url: response.data.logo_url,
        user_name: response.data.user?.name,
        user_picture: response.data.user?.picture,
        slug: response.data.slug
      });
    } catch (error) {
      console.error("Failed to fetch branding:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [brandingRes, statusRes] = await Promise.all([
          axios.get(`${API}/photographer/profile`, { withCredentials: true }),
          axios.get(`${API}/billing/account-status`, { withCredentials: true })
        ]);
        
        setBranding({
          company_name: brandingRes.data.company_name,
          logo_url: brandingRes.data.logo_url,
          user_name: brandingRes.data.user?.name,
          user_picture: brandingRes.data.user?.picture,
          slug: brandingRes.data.slug
        });
        
        setAccountStatus(statusRes.data);
        
        // Check agreement status (only for non-onboarding users)
        if (!location.pathname.includes("/onboarding")) {
          try {
            const agreementRes = await axios.get(
              `${API}/legal/photographer-agreement/status`,
              { withCredentials: true }
            );
            if (agreementRes.data.needs_reacknowledgement) {
              setShowAgreementModal(true);
            }
          } catch (error) {
            console.error("Failed to check agreement status:", error);
          }
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      }
    };
    
    loadData();
  }, [location.pathname]);

  // Redirect if on restricted path
  useEffect(() => {
    if (accountStatus.account_status === "restricted" || accountStatus.account_status === "suspended") {
      if (!isPathAllowed(location.pathname)) {
        toast.error("Your account access is restricted. Please update your payment method.");
        navigate("/dashboard/billing");
      }
    }
  }, [location.pathname, accountStatus.account_status, isPathAllowed, navigate]);

  // Generate the site link URL
  const getSiteLink = () => {
    // ALWAYS use the persisted slug field from the database
    if (branding.slug) {
      return `/photographer/${branding.slug}`;
    }
    return "/";
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Feature gating hook
  const { hasFeature, getUpgradeCTA, isUpgradePending, effectiveTier } = useFeatureGate();

  const navItems = [
    { to: "/dashboard", icon: <LayoutDashboard className="w-5 h-5" />, label: "Overview", end: true },
    { to: "/dashboard/requests", icon: <Users className="w-5 h-5" />, label: "Requests" },
    { to: "/dashboard/calendar", icon: <Calendar className="w-5 h-5" />, label: "Calendar" },
    { to: "/dashboard/galleries", icon: <Image className="w-5 h-5" />, label: "Galleries" },
    { to: "/dashboard/payments", icon: <Wallet className="w-5 h-5" />, label: "Payments", feature: "accept_payments" },
    { to: "/dashboard/quotes-invoices", icon: <FileText className="w-5 h-5" />, label: "Quotes & Invoices" },
    { to: "/dashboard/promo-codes", icon: <Tag className="w-5 h-5" />, label: "Promo Codes", feature: "sell_downloads" },
    { to: "/dashboard/contracts", icon: <FileSignature className="w-5 h-5" />, label: "Contracts" },
    { to: "/dashboard/email-templates", icon: <Mail className="w-5 h-5" />, label: "Email Templates" },
    { to: "/dashboard/analytics", icon: <BarChart3 className="w-5 h-5" />, label: "Analytics", feature: "analytics" },
    { to: "/dashboard/billing", icon: <CreditCard className="w-5 h-5" />, label: "Subscription" },
    { to: "/dashboard/profile", icon: <Settings className="w-5 h-5" />, label: "Profile & Branding" },
    { to: "/help", icon: <HelpCircle className="w-5 h-5" />, label: "Help" },
  ];

  // Helper to check if a nav item is gated
  const isNavItemLocked = (item) => {
    if (!item.feature) return false;
    return !hasFeature(item.feature);
  };

  // Helper to get lock indicator for nav item
  const getNavItemLockIcon = (item) => {
    if (!item.feature) return null;
    if (hasFeature(item.feature)) return null;
    
    const cta = getUpgradeCTA(item.feature);
    if (cta.type === 'pending') {
      return <Clock className="w-4 h-4 text-amber-500" />;
    }
    return <Lock className="w-4 h-4 text-muted-foreground" />;
  };

  // Helper to handle locked nav item click
  const handleLockedNavClick = (e, item) => {
    if (!isNavItemLocked(item)) return;
    
    e.preventDefault();
    const cta = getUpgradeCTA(item.feature);
    
    if (cta.type === 'pending') {
      toast.info("Upgrade pending confirmation", {
        description: "Your upgrade is being processed. Features will unlock shortly."
      });
      return;
    }
    
    if (cta.type === 'upgrade') {
      navigate("/pricing");
      return;
    }
    
    if (cta.type === 'contact') {
      navigate("/studio-contact");
      return;
    }
  };

  // Get branding display
  const brandName = branding.company_name || "360Lumina";
  const logoUrl = branding.logo_url ? `${API.replace("/api", "")}${branding.logo_url}` : null;

  // Get user avatar - prefer the one from profile (which has latest upload), fallback to auth user
  const userPicture = branding.user_picture || user?.picture;
  const avatarUrl = userPicture?.startsWith("/api/")
    ? `${API.replace("/api", "")}${userPicture}`
    : userPicture;

  return (
    <>
      {/* Agreement Re-acknowledgement Modal */}
      {showAgreementModal && (
        <AgreementReacknowledgementModal
          onComplete={() => setShowAgreementModal(false)}
        />
      )}
      
      {/* During onboarding, render only the content without sidebar */}
      {isOnboarding ? (
        <div className="min-h-screen">
          <Outlet context={{ branding, refreshBranding }} />
        </div>
      ) : (
      <div className="min-h-screen flex">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo / Branding */}
          <div className="p-6 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={brandName} 
                  className="h-8 max-w-[140px] object-contain"
                />
              ) : (
                <>
                  <Camera className="w-8 h-8 text-primary flex-shrink-0" />
                  <span className="text-xl font-serif truncate">{brandName}</span>
                </>
              )}
            </div>
            <button 
              className="lg:hidden text-muted-foreground hover:text-white flex-shrink-0 ml-2"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {/* Account Restriction Warning */}
            {(accountStatus.account_status === "restricted" || accountStatus.account_status === "suspended") && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                  <AlertTriangle className="w-4 h-4" />
                  {accountStatus.account_status === "suspended" ? "Account Suspended" : "Access Restricted"}
                </div>
                {accountStatus.days_until_suspension !== null && accountStatus.account_status === "restricted" && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {accountStatus.days_until_suspension} days until suspension
                  </p>
                )}
              </div>
            )}
            
            {navItems.map((item) => {
              const isRestricted = !isPathAllowed(item.to);
              const isLocked = isNavItemLocked(item);
              const lockIcon = getNavItemLockIcon(item);
              
              // Account restriction takes priority
              if (isRestricted) {
                return (
                  <div
                    key={item.to}
                    className="sidebar-link opacity-50 cursor-not-allowed flex items-center justify-between"
                    data-testid={`nav-${item.label.toLowerCase()}-disabled`}
                    onClick={(e) => {
                      e.preventDefault();
                      toast.error("This feature is locked. Please update your payment method.");
                    }}
                  >
                    <span className="flex items-center gap-2">
                      {item.icon}
                      {item.label}
                    </span>
                    <Lock className="w-4 h-4 text-muted-foreground" />
                  </div>
                );
              }
              
              // Feature-based gating (visible but locked)
              if (isLocked) {
                return (
                  <div
                    key={item.to}
                    className="sidebar-link opacity-60 cursor-pointer flex items-center justify-between hover:bg-muted/50"
                    data-testid={`nav-${item.label.toLowerCase()}-gated`}
                    onClick={(e) => handleLockedNavClick(e, item)}
                  >
                    <span className="flex items-center gap-2">
                      {item.icon}
                      {item.label}
                    </span>
                    {lockIcon}
                  </div>
                );
              }
              
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? "active" : ""}`
                  }
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="w-10 h-10">
                <AvatarImage src={avatarUrl} />
                <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-white"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 glass border-b border-white/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              className="lg:hidden text-muted-foreground hover:text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            {/* Mobile brand display */}
            <div className="lg:hidden flex items-center gap-2">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={brandName} 
                  className="h-6 max-w-[100px] object-contain"
                />
              ) : (
                <span className="text-lg font-serif">{brandName}</span>
              )}
            </div>
            
            <div className="flex items-center gap-4 ml-auto">
              <NotificationBell />
              <NavLink to={getSiteLink()} className="text-sm text-muted-foreground hover:text-white">
                View Site
              </NavLink>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          <Outlet context={{ branding, refreshBranding }} />
        </main>
      </div>
    </div>
      )}
    </>
  );
};

export default DashboardLayout;
